import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { inr } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PriorityPill, StatusPill } from "@/components/Pills";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Phone, MessageCircle, Snowflake, CalendarClock, AlertTriangle } from "lucide-react";

export default function FollowUps() {
  const navigate = useNavigate();
  const [data, setData] = useState({ today: [], overdue: [], cold: [] });
  const load = () => api.get("/followups").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const complete = async (c) => {
    const pending = (c.followups || []).find((f) => f.status === "pending");
    if (pending) { await api.patch(`/customers/${c.id}/followups/${pending.id}/complete`); load(); }
  };

  const Row = ({ c, overdue }) => (
    <Card className={`p-4 flex flex-wrap items-center gap-4 ${overdue ? "border-rose-200 bg-rose-50/40" : ""}`} data-testid={`fu-row-${c.id}`}>
      <div className="flex-1 min-w-[180px] cursor-pointer" onClick={() => navigate(`/app/customers/${c.id}`)}>
        <div className="font-semibold text-slate-900">{c.business_name}</div>
        <div className="text-xs text-slate-500">{c.contact_person} · {c.mobile}</div>
      </div>
      <div className="text-sm text-slate-600">{c.next_followup_date && <span className={overdue ? "text-rose-600 font-semibold" : ""}>{c.next_followup_date}</span>}</div>
      <PriorityPill value={c.priority} />
      <StatusPill value={c.lead_status} />
      <div className="flex gap-2">
        <a href={`https://wa.me/${(c.whatsapp || c.mobile).replace(/\D/g, "")}`} target="_blank" rel="noreferrer">
          <Button size="sm" variant="outline" data-testid={`wa-${c.id}`}><MessageCircle size={14} /></Button>
        </a>
        <a href={`tel:${c.mobile}`}><Button size="sm" variant="outline" data-testid={`call-${c.id}`}><Phone size={14} /></Button></a>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => complete(c)} data-testid={`done-${c.id}`}>Mark Done</Button>
      </div>
    </Card>
  );

  const Empty = ({ text }) => <div className="text-center text-slate-400 py-12">{text}</div>;

  return (
    <div>
      <PageHeader title="Follow-ups" subtitle="Stay on top of every promise you made." />
      <Tabs defaultValue="today">
        <TabsList data-testid="followup-tabs">
          <TabsTrigger value="today" data-testid="fu-tab-today"><CalendarClock size={15} className="mr-2" />Today ({data.today.length})</TabsTrigger>
          <TabsTrigger value="overdue" data-testid="fu-tab-overdue"><AlertTriangle size={15} className="mr-2" />Overdue ({data.overdue.length})</TabsTrigger>
          <TabsTrigger value="cold" data-testid="fu-tab-cold"><Snowflake size={15} className="mr-2" />Cold Leads ({data.cold.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="today" className="space-y-3 mt-4">
          {data.today.map((c) => <Row key={c.id} c={c} />)}
          {data.today.length === 0 && <Empty text="No follow-ups due today. 🎉" />}
        </TabsContent>
        <TabsContent value="overdue" className="space-y-3 mt-4">
          {data.overdue.map((c) => <Row key={c.id} c={c} overdue />)}
          {data.overdue.length === 0 && <Empty text="Nothing overdue. Great job!" />}
        </TabsContent>
        <TabsContent value="cold" className="space-y-3 mt-4">
          {data.cold.map((c) => <Row key={c.id} c={c} />)}
          {data.cold.length === 0 && <Empty text="No cold leads to nudge." />}
        </TabsContent>
      </Tabs>
    </div>
  );
}
